import { computed, Injectable, signal, WritableSignal } from '@angular/core'

import { ChatMessage } from '@yoshu-lib/models/chat-message.model'
import { ChatParticipant } from '@yoshu-lib/models/chat-participant.model'

import { UploadedDocument } from '@yoshu-lib/components/interaction-input/interaction-input.model'

export interface InitialPrompt {
  message: string
  images?: string[]
  documents?: UploadedDocument[]
  action?: string
}

@Injectable({ providedIn: 'root' })
export class ChatStore {
  private readonly messagesMap = signal<Record<string, ChatMessage[]>>({})
  private readonly participantsMap = signal<Record<string, ChatParticipant[]>>({})
  private readonly workflowMessagesMap = signal<
    Record<string, Record<string, WritableSignal<ChatMessage[]>>>
  >({})
  private readonly initialPrompt = signal<InitialPrompt | null>(null)
  private readonly agentResource = signal<string>('')

  // messages (all)
  readonly messagesForChat = computed(() => (chatId: string) => this.messagesMap()[chatId] || [])

  setMessages(chatId: string, messages: ChatMessage[]) {
    this.messagesMap.update((map) => ({ ...map, [chatId]: messages }))
    this.updateWorkflowMessages(chatId, messages)
  }

  addMessage(chatId: string, message: ChatMessage) {
    this.messagesMap.update((map) => {
      const messages = map[chatId] || []
      const index = messages.findIndex((m) => m.id === message.id)

      const updated =
        index !== -1
          ? [
              ...messages.slice(0, index),
              { ...messages[index], ...message },
              ...messages.slice(index + 1)
            ]
          : [...messages, message]

      this.updateWorkflowMessages(chatId, updated)

      return {
        ...map,
        [chatId]: updated
      }
    })
  }

  removeMessage(chatId: string, messageId: string) {
    this.messagesMap.update((map) => {
      const messages = map[chatId] || []
      const filtered = messages.filter((m) => m.id !== messageId)
      this.updateWorkflowMessages(chatId, filtered)
      return { ...map, [chatId]: filtered }
    })
  }

  clearMessages(chatId: string) {
    this.messagesMap.update((map) => {
      const { [chatId]: _, ...rest } = map
      return rest
    })

    this.workflowMessagesMap.update((map) => {
      const { [chatId]: _, ...rest } = map
      return rest
    })
  }

  getWorkflowGroups(chatId: string): string[] {
    console.log('getWorkflowGroups', chatId)
    console.log(this.workflowMessagesMap())
    return Object.keys(this.workflowMessagesMap()[chatId] || {})
  }

  // messages per workflow
  private updateWorkflowMessages(chatId: string, messages: ChatMessage[]) {
    let currentWorkflow = ''
    let groupIndex = 0
    const groups: Record<string, ChatMessage[]> = {}

    for (const msg of messages) {
      if (msg.workflow) {
        currentWorkflow = msg.workflow
        groupIndex++
      }
      const workflowKey = `${currentWorkflow || 'default'}-${groupIndex}`
      if (!groups[workflowKey]) groups[workflowKey] = []
      groups[workflowKey].push(msg)
    }

    this.workflowMessagesMap.update((map) => {
      const chatGroups: Record<string, WritableSignal<ChatMessage[]>> = map[chatId] || {}

      for (const [workflowKey, msgs] of Object.entries(groups)) {
        if (!chatGroups[workflowKey]) {
          chatGroups[workflowKey] = signal<ChatMessage[]>([])
        }
        chatGroups[workflowKey].set(msgs)
      }

      return { ...map, [chatId]: chatGroups }
    })
  }

  getMessagesSignal(chatId: string, workflowKey: string): WritableSignal<ChatMessage[]> {
    const current = this.workflowMessagesMap()
    if (!current[chatId]) {
      this.workflowMessagesMap.update((m) => ({ ...m, [chatId]: {} }))
    }

    const chatGroups = this.workflowMessagesMap()[chatId] || {}
    let sig = chatGroups[workflowKey]

    if (!sig) {
      sig = signal<ChatMessage[]>([])
      this.workflowMessagesMap.update((m) => ({
        ...m,
        [chatId]: { ...chatGroups, [workflowKey]: sig }
      }))
    }

    return sig
  }

  // setMessagesForWorkflow(chatId: string, workflow: string, messages: ChatMessage[]) {
  //   this.getMessagesSignal(chatId, workflow).set(messages)
  // }

  // addMessageForWorkflow(chatId: string, workflow: string, message: ChatMessage) {
  //   const sig = this.getMessagesSignal(chatId, workflow)
  //   const current = sig()
  //   const index = current.findIndex((m) => m.id === message.id)

  //   if (index !== -1) {
  //     const merged = { ...current[index], ...message }
  //     sig.set([...current.slice(0, index), merged, ...current.slice(index + 1)])
  //   } else {
  //     sig.set([...current, message])
  //   }
  // }

  clearWorkflowMessages(chatId: string, workflowKey?: string) {
    this.workflowMessagesMap.update((map) => {
      if (!map[chatId]) return map

      if (workflowKey) {
        const { [workflowKey]: _, ...restGroups } = map[chatId]
        return { ...map, [chatId]: restGroups }
      }

      const { [chatId]: _, ...rest } = map
      return rest
    })
  }

  setInitialPrompt(value: InitialPrompt | null) {
    this.initialPrompt.set(value)
  }

  getInitialPrompt() {
    return this.initialPrompt()
  }

  // participants

  readonly participantsForChat = computed(
    () => (chatId: string) => this.participantsMap()[chatId] || []
  )

  setParticipants(chatId: string, participants: ChatParticipant[]) {
    this.participantsMap.update((map) => ({ ...map, [chatId]: participants }))
  }

  addParticipant(chatId: string, participant: ChatParticipant) {
    this.participantsMap.update((map) => {
      const existing = map[chatId] || []
      return {
        ...map,
        [chatId]: [...existing, participant]
      }
    })
  }

  removeParticipant(chatId: string, participantId: string) {
    this.participantsMap.update((map) => {
      const existing = map[chatId] || []
      return {
        ...map,
        [chatId]: existing.filter((p) => p.id !== participantId)
      }
    })
  }

  clearParticipants(chatId: string) {
    this.participantsMap.update((map) => {
      const { [chatId]: _, ...rest } = map
      return rest
    })
  }

  setAgentResource(value: string) {
    this.agentResource.set(value)
  }

  getAgentResource() {
    return this.agentResource()
  }
}
